import { TOOL_NAMES } from 'js/src/agent/toolNames';
import { z } from 'zod';

import type { EnhancedToolResult, ToolExecutionContext } from '../../types';
import { VideoStateTool } from '../VideoStateTool';

import { getBrandKitById } from 'js/src/data/brand-kits/brand-kits-selectors';
import { applyBrandKitOperation } from 'js/src/luminary';
import { makeOperationsCallable } from 'js/src/luminary/luminary-hooks';
import { isLuminaryEmpty } from 'js/src/luminary/luminary-selectors';
import { SubagentAccess } from 'js/src/agent/subagents/subagentAccess';

export const applyBrandKitSchema = z.object({
    brandKitId: z.number().int().describe('The id of the brand kit to apply.'),
});
export type ApplyBrandKitArgs = z.infer<typeof applyBrandKitSchema>;

export class ApplyBrandKitTool extends VideoStateTool<typeof applyBrandKitSchema> {
    protected override readonly schema = applyBrandKitSchema;
    protected override readonly toolName = TOOL_NAMES.applyBrandKit;
    public override readonly subagentAccess = SubagentAccess.BLOCKED;
    protected override readonly toolDescription = `Apply a saved brand kit (its colors, fonts, and watermark) to the current video. The video must already have at least one scene — build it first, then apply so everything inherits the kit. Use ${TOOL_NAMES.listBrandKits} first to find the brand kit's id.`;

    public override getToolDisplayName(): string {
        return 'Applying brand kit';
    }

    public get statusIcon(): string {
        return 'palette';
    }

    public override async execute(
        args: ApplyBrandKitArgs,
        context: ToolExecutionContext,
    ): Promise<EnhancedToolResult> {
        const { brandKitId } = args;

        if (isLuminaryEmpty(this.luminary)) {
            return {
                result: `The video has no scenes yet, so there is nothing to apply a brand kit to. Build the video first (add at least one scene), then call ${TOOL_NAMES.applyBrandKit} — everything built afterwards inherits the kit.`,
                isSuccess: false,
            };
        }

        const brandKit = getBrandKitById(this.getState(), brandKitId);
        if (!brandKit) {
            return {
                result: `Error: No brand kit found with id ${brandKitId}. Use ${TOOL_NAMES.listBrandKits} to see available brand kits.`,
                isSuccess: false,
            };
        }

        const { applyBrandKit } = makeOperationsCallable(
            { applyBrandKitOperation },
            this.luminary,
            this.dispatch,
            () => this.getState(),
        );

        applyBrandKit(brandKitId, {
            systemTriggered: false,
            parentActionGroup: context.parentActionGroup,
        });

        return {
            result: `Successfully applied brand kit "${brandKit.name}" to the video.`,
            isSuccess: true,
        };
    }
}
